import React, { useMemo } from "react";
import { STADIUM_VIEWBOX, ZONE_PATHS, FLOW_VECTORS, pathCentroid } from "@/data/stadiumPaths";

/**
 * Stadium top-down with density heatmap overlay + animated flow arrows.
 * heatmapData: { [zoneId]: { pct, occupancy, capacity, name } }
 * flows: [{ id, from, to, intensity }]
 */
const heatColor = (pct) => {
  if (pct >= 0.85) return "hsl(var(--destructive))";
  if (pct >= 0.7)  return "hsl(var(--warning))";
  if (pct >= 0.5)  return "hsl(var(--primary))";
  if (pct >= 0.3)  return "hsl(217 91% 60%)"; // cool blue
  return "hsl(var(--muted-foreground))";
};

export const HeatmapStadium = ({ zones = [], flows = [], onZoneClick }) => {
  const byId = useMemo(() => Object.fromEntries(zones.map((z) => [z.zone_id || z.id, z])), [zones]);
  const flowD = useMemo(() => Object.fromEntries(FLOW_VECTORS.map((f) => [f.id, f])), []);

  return (
    <div className="w-full h-full relative">
      <svg viewBox={STADIUM_VIEWBOX} className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-testid="heatmap-stadium-svg">
        <defs>
          <radialGradient id="hmPitchGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.16" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Stadium blueprint backdrop (Wankhede Stadium) */}
        <image
          href="/stadium-backdrop.png"
          x="0" y="0" width="1000" height="720"
          preserveAspectRatio="xMidYMid meet"
          opacity="0.18"
          style={{ pointerEvents: "none" }}
        />

        {/* Pitch */}
        <ellipse cx="500" cy="360" rx="220" ry="160" fill="hsl(var(--surface-elevated))" stroke="hsl(var(--border))" strokeWidth="1.5" />
        <ellipse cx="500" cy="360" rx="220" ry="160" fill="url(#hmPitchGlow)" />
        <rect x="486" y="320" width="28" height="80" fill="hsl(var(--muted))" stroke="hsl(var(--border))" />
        <text x="500" y="365" textAnchor="middle" className="font-mono" fontSize="10" fill="hsl(var(--muted-foreground))" letterSpacing="3">Pitch</text>

        {/* Zones colored by density */}
        {Object.entries(ZONE_PATHS).map(([zid, d]) => {
          const z = byId[zid];
          const pct = z?.pct ?? 0;
          const fill = heatColor(pct);
          const c = pathCentroid(d);
          return (
            <g key={zid} className="cursor-pointer" onClick={() => onZoneClick?.(zid)} data-testid={`heat-zone-${zid}`}>
              <path
                d={d}
                fill={fill}
                fillOpacity={0.15 + pct * 0.65}
                stroke={fill}
                strokeOpacity={0.75}
                strokeWidth={1}
              />
              {pct >= 0.85 && (
                <path d={d} fill="none" stroke="hsl(var(--destructive))" strokeWidth="1.5" className="flow-path" />
              )}
              {z && (
                <text x={c.x} y={c.y - 4} textAnchor="middle" fontSize="9" fill="hsl(var(--foreground))" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">
                  {z.name}
                </text>
              )}
              {z && (
                <text x={c.x} y={c.y + 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="hsl(var(--foreground))" fontFamily="IBM Plex Mono, monospace">
                  {Math.round(pct * 100)}%
                </text>
              )}
            </g>
          );
        })}

        {/* Flow arrows */}
        {flows.map((f) => {
          const path = flowD[f.id];
          if (!path) return null;
          const stroke = f.intensity >= 0.75 ? "hsl(var(--destructive))" :
                         f.intensity >= 0.55 ? "hsl(var(--warning))" :
                                                "hsl(var(--primary))";
          return (
            <g key={f.id}>
              <path d={path.d} fill="none" stroke={stroke} strokeWidth={1 + f.intensity * 2} className="flow-path" opacity={0.45 + f.intensity * 0.45} />
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 panel-elevated px-2 py-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
        <span>density</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--muted-foreground))" }} /> &lt;30</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(217 91% 60%)" }} /> 30+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--primary))" }} /> 50+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--warning))" }} /> 70+</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "hsl(var(--destructive))" }} /> 85+</span>
      </div>
    </div>
  );
};

export default HeatmapStadium;
